// @ts-nocheck -- copied verbatim from the application being replaced.
//
// Quarantined, not exempted. This file has not been rewritten to this project's
// settings and does not pass them; checking it would report errors nobody is
// going to fix in place, and loosening the settings to accommodate it would
// apply that to code written here too. Vite still compiles it -- esbuild strips
// types without checking them.
//
// Delete this line when the file is rewritten. It is the marker for what is left
// to do, and the count of files carrying it is the size of the refactor.
/**
 * Unified Authentication Storage Service
 * 
 * This service provides a single source of truth for all authentication-related data.
 * It replaces the fragmented approach of storing tokens, user info, and brand data
 * across multiple localStorage keys.
 * 
 * Key Features:
 * - Single localStorage key for all auth data
 * - Type-safe interfaces matching API response
 * - Token expiration management
 * - Automatic migration from legacy storage
 * - Debug utilities
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

import { ModuleSubscriptionDto } from '../types/plan.types';
import { logger } from '@/shared/observability/logger';
import { tokenStorage } from '@/shared/auth/tokenStorage';
/**
 * Role information from API
 */
export interface RoleInfo {
  roleId: number;
  roleName: string;
  description: string;
  permissions: string[];
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
}

/**
 * Company information from API
 */
export interface CompanyInfo {
  companyId: number;
  brandId?: number;  // Add brandId field to company object
  companyName: string;
  domainName: string;
  websiteUrl: string;
  companyLogoUrl: string | null;
  status: string;
  totalReviews?: number;
  city?: string;
}

/**
 * User information from API (userInfo object in login response)
 */
export interface UserInfo {
  id: string;              // Employee/Brand ID 
  userId: string;          // Internal UUID 
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string | null;
  profilePictureUrl: string | null;
  role: RoleInfo;
  company: CompanyInfo;
  emailVerified: boolean;
  isActive: boolean;
  createdAt: string;
  lastLogin: string | null;
  location: string | null;
  bio: string | null;      // Add bio field
  roleInCompany: string | null; // Add roleInCompany field
  timezone: string | null;
  language: string;
  preferences: Record<string, any>;
   theme?: string;
}

/**
 * Complete login API response structure
 */
export interface LoginApiResponse {
  token: string;           // JWT access token
  refreshToken: string;    // JWT refresh token
  brandId: string;         // Brand identifier (e.g., "MRTFY000006")
  expirationTime: number;  // Token validity in seconds
  userInfo: UserInfo;      // Complete user information
  currentPlan?: string;    // Current plan identifier from API (e.g., "premium", "free")
  planDisplayName?: string; // Display name for the plan (e.g., "Premium Plan", "Free Plan")
  activeModules?: ModuleSubscriptionDto[]; // Active module subscriptions
}

/**
 * Simplified User object for components
 */
export interface User {
  id: string;              // Employee/Brand ID
  userId: string;          // Internal UUID
  username: string;
  email: string;
  name: string; // Combined firstName + lastName
  firstName: string;
  lastName: string;
  plan: string; // Derived from role or API planDisplayName
  planDisplayName?: string; // Display name from API
  image?: string;
  phoneNumber?: string;
  location?: string;
  bio?: string;            // Add bio field
  roleInCompany?: string;  // Add roleInCompany field
  role: string;
  roleDisplayName: string;
  emailVerified: boolean;
  isActive: boolean;
  company: CompanyInfo;
  hasDeveloperAccess?: boolean;
  isAgencyUser?: boolean;  // true when stored token carries agency_id/member_id claims
   theme?: string;
   currentPlan?:any;
}

/**
 * Unified authentication data stored in localStorage
 */
export interface UnifiedAuthData {
  token: string;
  refreshToken: string;
  brandId: string;
  expirationTime: number;
  userInfo: UserInfo;
  currentPlan?: string;      // Current plan from API
  planDisplayName?: string;  // Plan display name from API
  activeModules?: ModuleSubscriptionDto[]; // Active module subscriptions  
  // Metadata
  loginTimestamp: number;    // When user logged in
  lastActivityTimestamp: number; // Last activity timestamp
}

// ============================================================================
// UNIFIED AUTH STORAGE CLASS
// ============================================================================

const STORAGE_KEY = 'xtremand_auth';
const SCOPED_AGENCY_TOKEN_KEY = 'xtremand_scoped_agency_token';
const SCOPED_AGENCY_REFRESH_TOKEN_KEY = 'xtremand_scoped_agency_refresh_token';
// Keys to PURGE, not to read. These were written by the previous product and
// are in visitors' browsers; the names are theirs and must stay spelled their
// way, or the clear-up stops clearing anything up. The three keys above were
// renamed on 17 October 2026 and their previous spellings are copied forward
// once by shared/storage/legacyStorageMigration.ts -- these six are not, because
// nothing has read them since before that rename.
const LEGACY_KEYS = [
  'rivo9_jwt_token',
  'rivo9_refresh_token',
  'rivo9_brand_id',
  'rivo9_user_data',
  'rivo9_auth_state',
  'rivo9_profile_data' // Legacy profile data
];

// The pre-rename spellings of STORAGE_KEY and the two scoped-agency keys are
// DELIBERATELY NOT in that list. This class purges LEGACY_KEYS whenever it finds
// no unified session -- which is exactly the state a visitor is in on their first
// load after the rename -- so listing them here would delete the session a moment
// before the migration copied it, which is the one outcome the rename had to
// avoid. They are left on disk instead: a rollback to the previous bundle then
// still finds its session, and the copy is skipped on every later start because
// the current key now holds a value.

let currentStorageMode: 'localStorage' | 'sessionStorage' = 'localStorage';

/**
 * Unified Authentication Storage
 * Single source of truth for all authentication data
 */
export class UnifiedAuthStorage {
  /**
   * Set storage mode for this session (localStorage or sessionStorage)
   */
  static setStorageMode(useLocalStorage: boolean): void {
    currentStorageMode = useLocalStorage ? 'localStorage' : 'sessionStorage';
    logger.debug(`🔧 Storage mode set to: ${currentStorageMode}`);
  }

  /**
   * Get active storage instance
   */
  private static getStorage(): Storage {
    return currentStorageMode === 'localStorage' ? localStorage : sessionStorage;
  }

  /**
   * Store complete authentication data
   */
  static setAuthData(response: LoginApiResponse): void {
    const authData: UnifiedAuthData = {
      token: response.token,
      refreshToken: response.refreshToken,
      brandId: response.brandId,
      expirationTime: response.expirationTime,
      userInfo: response.userInfo,
      currentPlan: response.currentPlan,
      planDisplayName: response.planDisplayName,
      activeModules: response.activeModules,
      loginTimestamp: Date.now(),
      lastActivityTimestamp: Date.now()
    };

    try {
      const storage = this.getStorage();
      // const otherStorage = currentStorageMode === 'localStorage' ? sessionStorage : localStorage;

      // otherStorage.removeItem(STORAGE_KEY);
      storage.setItem(STORAGE_KEY, JSON.stringify(authData));
      logger.debug(`✅ Unified auth data stored successfully in ${currentStorageMode}`);
    } catch (error) {
      logger.error('❌ Failed to store auth data:', error);
      throw new Error('Failed to store authentication data');
    }
  }

  /**
   * Retrieve complete authentication data.
   * Always reads from localStorage first (auth is always persisted there).
   * Falls back to sessionStorage only as a read-only fallback without
   * mutating the module-level currentStorageMode variable.
   */
  static getAuthData(): UnifiedAuthData | null {
    try {
      // Always prefer localStorage so auth is visible across tabs
      let data = localStorage.getItem(STORAGE_KEY);
      let targetStorage: Storage = localStorage;

      // Fallback to sessionStorage (read-only, does not change currentStorageMode)
      if (!data) {
        data = sessionStorage.getItem(STORAGE_KEY);
        targetStorage = sessionStorage;
      }

      if (!data) return null;

      const authData: UnifiedAuthData = JSON.parse(data);

      // Update last activity timestamp
      authData.lastActivityTimestamp = Date.now();
      targetStorage.setItem(STORAGE_KEY, JSON.stringify(authData));

      return authData;
    } catch (error) {
      logger.error('❌ Failed to retrieve auth data:', error);
      return null;
    }
  }

  /**
   * Update tokens (useful for token refresh)
   */
  static updateTokens(token: string, refreshToken: string, expirationTime: number): void {
    const authData = this.getAuthData();
    if (!authData) {
      logger.error('❌ Cannot update tokens: No auth data found');
      return;
    }

    authData.token = token;
    authData.refreshToken = refreshToken;
    authData.expirationTime = expirationTime;
    authData.lastActivityTimestamp = Date.now();

    try {
      // const storage = this.getStorage();
      // const otherStorage = currentStorageMode === 'localStorage' ? sessionStorage : localStorage;

      // otherStorage.removeItem(STORAGE_KEY);
      // storage.setItem(STORAGE_KEY, JSON.stringify(authData));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(authData));
      logger.debug('✅ Tokens updated successfully');
    } catch (error) {
      logger.error('❌ Failed to update tokens:', error);
    }
  }

  /**
   * Clear all authentication data
   */
  static clearAuthData(): void {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    
    // Also clear legacy keys for complete cleanup
    LEGACY_KEYS.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    
    localStorage.removeItem(SCOPED_AGENCY_TOKEN_KEY);
    sessionStorage.removeItem(SCOPED_AGENCY_TOKEN_KEY);
    localStorage.removeItem(SCOPED_AGENCY_REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(SCOPED_AGENCY_REFRESH_TOKEN_KEY);

    logger.debug('✅ All auth data cleared');
  }

  /**
   * Check if user is authenticated.
   * NOTE: Does NOT clear storage as a side-effect. If the token is expired,
   * it simply returns false. The SessionValidator / logout flow is responsible
   * for clearing storage, so that all tabs are handled uniformly.
   */
  static isAuthenticated(): boolean {
    if (tokenStorage.accessToken()) return true;

    const authData = this.getAuthData();
    if (!authData) return false;

    // Check if token is expired
    if (this.isTokenExpired()) {
      logger.warn('⚠️ Token expired, clearing auth data');
      this.clearAuthData();
      return false;
    }

    return true;
  }

  /**
   * Get JWT access token
   */
  static getToken(): string | null {
    // The session this application signs in with lives in `tokenStorage`; the legacy store is
    // only a fallback for data an older sign-in left behind. Reading only the legacy key
    // sent every ported screen's request without a token (401).
    const authData = this.getAuthData();
    return tokenStorage.accessToken() || authData?.token || null;
  }

  /**
   * Get refresh token
   */
  static getRefreshToken(): string | null {
    const authData = this.getAuthData();
    return tokenStorage.refreshToken() || authData?.refreshToken || null;
  }

  /**
   * Get brand ID
   */
  static getBrandId(): string | null {
    const authData = this.getAuthData();
    return authData?.brandId || null;
  }

  /**
   * Get complete user info from API response
   */
  static getUserInfo(): UserInfo | null {
    const authData = this.getAuthData();
    return authData?.userInfo || null;
  }

  /**
   * Get simplified User object for components
   */
  static getUser(): User | null {
    const authData = this.getAuthData();
    if (!authData) return null;

    const userInfo = authData.userInfo;

    // Prioritize API's planDisplayName, fallback to role-based mapping
    const rawPlanDisplayName = authData.planDisplayName || this.mapRoleToPlan(userInfo.role?.roleName ?? '');
    
    // Normalize plan display name for consistent formatting (e.g., "Pro" -> "PRO Plan")
    const planDisplayName = this.normalizePlanDisplayName(rawPlanDisplayName);

    // Construct full name with proper fallback handling
    const fullName = `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim() || userInfo.username || 'User';

    return {
      id: userInfo.id,
      userId: userInfo.userId,
      username: userInfo.username,
      email: userInfo.email,
      name: fullName,
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      plan: planDisplayName,
      planDisplayName: planDisplayName,
      image: this.sanitizeProfilePictureUrl(userInfo.profilePictureUrl),
      phoneNumber: userInfo.phoneNumber || undefined,
      location: userInfo.location || undefined,
      bio: userInfo.bio || undefined,
      roleInCompany: userInfo.roleInCompany || undefined,
      role: userInfo.role?.roleName ?? '',
      roleDisplayName: userInfo.role?.description ?? '',
      emailVerified: userInfo.emailVerified,
      isActive: userInfo.isActive,
      company: userInfo.company,
      hasDeveloperAccess: (userInfo as any).hasDeveloperAccess,
      isAgencyUser: this.isAgencyToken()
    };
  }

  /**
   * Always false, and it always was.
   *
   * This read `agency_id` / `member_id` claims out of the stored access token
   * until 17 October 2026. This backend issues **opaque** access tokens through
   * a local introspector -- there are no claims in one, so the decode threw, the
   * decoder returned null, and this returned false on every call. Removing the
   * decode changes no behaviour; it removes the last place in this application
   * that treated an access token as something it could read.
   *
   * It stays rather than being deleted because `getUser()` populates
   * `isAgencyUser` from it and `AuthProvider` reads that. If agency context is
   * ever needed, it has to come from the server -- a client cannot verify a
   * claim it decodes for itself, which is the whole reason the tokens are
   * opaque.
   */
  static isAgencyToken(): boolean {
    return false;
  }

  /**
   * Sanitize profile picture URL to ensure it's properly formatted
   */
  private static sanitizeProfilePictureUrl(url: string | null): string | undefined {
    if (!url) return undefined;
    
    // Google profile pictures sometimes have malformed query params
    // Fix URLs like "...=s96-c" to proper format
    if (url.includes('googleusercontent.com') && url.includes('=s')) {
      // Already has proper format, return as-is
      return url;
    }
    
    return url;
  }

  /**
   * Get company information
   */
  static getCompany(): CompanyInfo | null {
    const authData = this.getAuthData();
    return authData?.userInfo?.company || null;
  }

  /**
   * Get company brand ID (from company object in login response)
   */
  static getCompanyBrandId(): number | null {
    const company = this.getCompany();
    return company?.brandId || company?.companyId || null;
  }

  /**
   * Get total reviews count
   */
  static getTotalReviews(): number | null {
    const authData = this.getAuthData();
    return authData?.userInfo?.company?.totalReviews || null;
  }

  /**
   * Map role name to plan display name
   */
  private static mapRoleToPlan(roleName: string): string {
    const planMap: Record<string, string> = {
      'ROLE_ADMIN': 'Enterprise Plan',
      'ROLE_USER': 'Pro Plan',
      'ROLE_PREMIUM': 'Premium Plan',
      'ROLE_FREE': 'Free Plan',
      'GUEST': 'Guest'
    };
    return planMap[roleName] || 'Free Plan';
  }

  /**
   * Normalize plan display name to ensure consistent formatting
   * Ensures plan names have " Plan" suffix and proper casing
   */
  private static normalizePlanDisplayName(planName: string | undefined): string {
    if (!planName) return 'Free Plan';
    
    // If already ends with " Plan", ensure proper casing
    if (planName.toLowerCase().endsWith(' plan')) {
      // Extract plan type and ensure it's uppercase
      const planType = planName.replace(/\s+plan$/i, '').trim().toUpperCase();
      return `${planType} Plan`;
    }
    
    // If it's a known plan type without " Plan" suffix, add it
    const knownPlans = ['FREE', 'STARTER', 'PRO', 'ENTERPRISE'];
    const upperPlanName = planName.trim().toUpperCase();
    
    if (knownPlans.includes(upperPlanName)) {
      return `${upperPlanName} Plan`;
    }
    
    // For unknown formats, just append " Plan" if not present
    return planName.includes('Plan') || planName.includes('plan') 
      ? planName 
      : `${planName} Plan`;
  }

  /**
   * Check if token is expired.
   * Guards against missing / zero expirationTime by defaulting to 24 hours.
   */
  static isTokenExpired(): boolean {
    const authData = this.getAuthData();
    if (!authData) return true;

    const now = Date.now();
    const loginTime = authData.loginTimestamp;
    // Default to 24 h if expirationTime is missing, zero, or negative
    const rawExpSeconds = authData.expirationTime;
    const effectiveExpSeconds = (rawExpSeconds && rawExpSeconds > 0) ? rawExpSeconds : 86400;
    const expirationMs = effectiveExpSeconds * 1000;
    const expiresAt = loginTime + expirationMs;

    return now >= expiresAt;
  }

  /**
   * Check if token is expiring soon (within 5 minutes)
   */
  static isTokenExpiringSoon(minutesThreshold: number = 5): boolean {
    const authData = this.getAuthData();
    if (!authData) return true;

    const now = Date.now();
    const loginTime = authData.loginTimestamp;
    const expirationMs = authData.expirationTime * 1000;
    const expiresAt = loginTime + expirationMs;
    const thresholdMs = minutesThreshold * 60 * 1000;

    return (expiresAt - now) <= thresholdMs;
  }

  /**
   * Get active module subscriptions
   */
  static getActiveModules(): ModuleSubscriptionDto[] {
    const authData = this.getAuthData();
    return authData?.activeModules || [];
  }

  /**
   * Get time until token expiration in milliseconds
   */
  static getTimeUntilExpiration(): number {
    const authData = this.getAuthData();
    if (!authData) return 0;

    const now = Date.now();
    const loginTime = authData.loginTimestamp;
    const expirationMs = authData.expirationTime * 1000;
    const expiresAt = loginTime + expirationMs;

    return Math.max(0, expiresAt - now);
  }

  /**
   * Get session duration in milliseconds
   */
  static getSessionDuration(): number {
    const authData = this.getAuthData();
    if (!authData) return 0;

    return Date.now() - authData.loginTimestamp;
  }

  /**
   * Get last activity timestamp
   */
  static getLastActivity(): number {
    const authData = this.getAuthData();
    return authData?.lastActivityTimestamp || 0;
  }

  /**
   * Migrate from legacy storage format
   * Returns true if migration was needed and performed
   */
  static migrateLegacyStorage(): boolean {
    // Check if already using unified storage
    if (this.getAuthData()) {
      logger.debug('✅ Already using unified auth storage');
      return false;
    }

    // Check if legacy data exists
    const hasLegacyData = LEGACY_KEYS.some(key => localStorage.getItem(key) !== null);
    
    if (hasLegacyData) {
      logger.warn('⚠️ Legacy auth storage detected - clearing old data');
      LEGACY_KEYS.forEach(key => localStorage.removeItem(key));
      logger.debug('✅ Legacy storage cleared - user will need to re-login');
      return true;
    }

    return false;
  }

  /**
   * Get authentication status for debugging
   */
  static getAuthStatus(): {
    isAuthenticated: boolean;
    hasToken: boolean;
    isExpired: boolean;
    isExpiringSoon: boolean;
    timeUntilExpiration: number;
    sessionDuration: number;
    user: User | null;
  } {
    return {
      isAuthenticated: this.isAuthenticated(),
      hasToken: !!this.getToken(),
      isExpired: this.isTokenExpired(),
      isExpiringSoon: this.isTokenExpiringSoon(),
      timeUntilExpiration: this.getTimeUntilExpiration(),
      sessionDuration: this.getSessionDuration(),
      user: this.getUser()
    };
  }

  /**
   * Export auth data for debugging (DO NOT use in production logs)
   */
  static exportAuthData(): any {
    const authData = this.getAuthData();
    if (!authData) return null;

    return {
      ...authData,
      token: authData.token.substring(0, 20) + '...[REDACTED]',
      refreshToken: authData.refreshToken.substring(0, 20) + '...[REDACTED]'
    };
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Handle successful login
 * Call this after receiving login API response
 * @param response - Login API response
 *
 * The rememberMe parameter went on 28 September 2026 with the checkbox. It only ever
 * reached a log line -- setStorageMode(true) below has always been unconditional, so
 * an unticked box was logged as a sessionStorage session and stored in localStorage.
 */
export function handleLoginSuccess(response: LoginApiResponse): User {
   //use localStorage to persist auth across tabs
  UnifiedAuthStorage.setStorageMode(true);
  logger.debug('🔑 handleLoginSuccess - Raw Response Check:', {
    hasActiveModules: !!response.activeModules,
    modulesCount: response.activeModules?.length || 0,
    moduleKeys: response.activeModules?.map(m => m.moduleKey)
  });
  
  // Store complete auth data
  UnifiedAuthStorage.setAuthData(response);
  
  // Return simplified user object
  const user = UnifiedAuthStorage.getUser();
  if (!user) {
    throw new Error('Failed to create user object from auth data');
  }
  
  logger.debug('✅ Login successful:', {
    username: user.username,
    email: user.email,
    role: user.role,
    brandId: response.brandId
  });

    // ✅ CRITICAL FIX: Notify ThemeProvider that auth has been updated (for immediate theme sync on login)
  if (typeof window !== 'undefined') {
    logger.debug('📢 [handleLoginSuccess] Dispatching auth-updated event for immediate theme sync');
    const event = new CustomEvent('xtremand-auth-updated', { 
      detail: { 
        userId: user.id,
        theme: user.theme || response.userInfo?.theme 
      } 
    });
    window.dispatchEvent(event);
  }
  
  return user;
}

/**
 * Resolve the default post-login route for the current user.
 * Gmail users should land in My Reviews instead of brand/company reviews.
 */
export function getPostLoginRedirectPath(
  requestedPath?: string | null,
): string {
  if (requestedPath && requestedPath.trim()) {
    return requestedPath;
  }

  return '/dashboard';
}

/**
 * Handle logout
 * Clears all authentication data
 */
export function handleLogout(): void {
  UnifiedAuthStorage.clearAuthData();
  localStorage.removeItem('chatSessionId');
  localStorage.removeItem('chatMessages');

  // Notify ThemeProvider to reset to the system theme now that the user session has ended
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('xtremand-logout'));
  }

  logger.debug('✅ Logout successful');
}

/**
 * Check if refresh is needed
 */
export function shouldRefreshToken(): boolean {
  return UnifiedAuthStorage.isTokenExpiringSoon(5);
}

// ============================================================================
// BACKWARD COMPATIBILITY WRAPPER (DEPRECATED)
// ============================================================================

/**
 * @deprecated Use UnifiedAuthStorage instead
 * This is kept for backward compatibility during migration
 */
export class TokenManager {
  static setToken(token: string): void {
    logger.warn('⚠️ TokenManager.setToken is deprecated. Use UnifiedAuthStorage.setAuthData() instead.');
  }

  static getToken(): string | null {
    return UnifiedAuthStorage.getToken();
  }

  static setBrandId(brandId: string): void {
    logger.warn('⚠️ TokenManager.setBrandId is deprecated. Use UnifiedAuthStorage.setAuthData() instead.');
  }

  static getBrandId(): string | null {
    return UnifiedAuthStorage.getBrandId();
  }

  static setUser(user: any): void {
    logger.warn('⚠️ TokenManager.setUser is deprecated. Use UnifiedAuthStorage.setAuthData() instead.');
  }

  static getUser(): any {
    return UnifiedAuthStorage.getUser();
  }

  static clearAll(): void {
    UnifiedAuthStorage.clearAuthData();
  }

  static isAuthenticated(): boolean {
    return UnifiedAuthStorage.isAuthenticated();
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export default UnifiedAuthStorage;